import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICampaignOverview } from '../../model/briefing/overview/ICampaignOverview';
import { IInviteContractInfluencer } from '../../model/book/Invites';
import { IInfluencerDistribution } from '../../app/influencer/offer/dto/IInfluencerDistribution';
import { WarningToast } from '../../utils/toasters';
import { IOfferItemsRequest } from '../../model/offer/IOfferItems';
import { User } from '../../model/user/User';
import { OfferStatusEnum } from '../../enum/OfferStatusEnum';
export interface IContractAgency {
    selectedMatchedUser?: User;
    activeTabModal: number;
    campaignId: string;
    campaign?: ICampaignOverview;
    isDistributionChecked: boolean;
    isLegalChecked: boolean;
    isStepperFinished: boolean;
    isEditable?: boolean;
    canCounter?: boolean;
    contractModalStatus: string;
    initialContract: IInviteContractInfluencer;
    additionalItemId: number;
    distributionArr: IInfluencerDistribution[];
    hasLegal: boolean;
    hasDistribution: boolean;
    requiredFieldChangeColor?: boolean;
    canWriteComment?: boolean;
    isCharity: boolean;
    hasUnreadComments: boolean;
    hasFee: boolean;
    isIvConnectedClub: boolean;
    feePercentage?: number;
    inviteAttachment?: Blob[] | MediaSource[];
}

const initialContractState: IContractAgency = {
    selectedMatchedUser: undefined,
    activeTabModal: 0,
    campaignId: '',
    canCounter: undefined,
    campaign: undefined,
    distributionArr: [],
    contractModalStatus: 'preview',
    isDistributionChecked: false,
    isLegalChecked: false,
    isStepperFinished: false,
    additionalItemId: 1,
    isEditable: undefined,
    initialContract: {
        description: '',
        personalOffer: '',
        price: 0,
        contractItems: [],
        distribution: [],
    },
    hasLegal: true,
    hasDistribution: false,
    requiredFieldChangeColor: false,
    canWriteComment: false,
    isCharity: false,
    hasUnreadComments: false,
    hasFee: false,
    isIvConnectedClub: false,
    inviteAttachment: undefined,
};
export const contractAgency = createSlice({
    name: 'agencyContract',
    initialState: initialContractState,
    reducers: {
        setActiveTab: (state, action) => {
            const currentStep = action.payload;
            if (!state.isDistributionChecked && currentStep > 2) {
                WarningToast('Please check the required fields');
                return { ...state, activeTabModal: 2, requiredFieldChangeColor: true };
            } else if (!state.isLegalChecked && currentStep > 3 && state.hasLegal) {
                WarningToast('Please check the required fields');
                return { ...state, activeTabModal: 3, requiredFieldChangeColor: true };
            } else if (!state.isStepperFinished && currentStep > 3) {
                return {
                    ...state,
                    isStepperFinished: true,
                    activeTabModal: 4,
                };
            }
            if (currentStep === 5) {
                return {
                    ...state,
                    requiredFieldChangeColor: false,
                    activeTabModal: action.payload,
                    hasUnreadComments: false,
                };
            }
            return { ...state, requiredFieldChangeColor: false, activeTabModal: action.payload };
        },
        setContractModalStatus: (state, action) => {
            return { ...state, contractModalStatus: action.payload };
        },
        setOfferModalDetails: (state, action) => {
            const campaign: ICampaignOverview = action.payload;
            const contractItems: IOfferItemsRequest[] = [];
            let additionalItems: IInfluencerDistribution[] = [];
            campaign.contractPlaceholder?.totalPlatforms?.map(platform =>
                platform.contractItems.map(item =>
                    contractItems.push({
                        mainPlatformId: platform.id,
                        socialPlatformId: item.socialPlatform.id,
                        nbPosts: item.nbPosts ?? 0,
                        price: item.price,
                        oldPrice: item.oldPrice,
                    })
                )
            );
            if (campaign?.contractPlaceholder?.additionalContractItems?.length) {
                campaign?.contractPlaceholder?.additionalContractItems.map(item => additionalItems.push(item));
            }
            if (!campaign.contracts.length) {
                additionalItems = [...campaign?.contractPlaceholder?.distribution];
            }
            return {
                ...state,
                campaign: action.payload,
                activeTabModal: campaign?.contracts?.length && 4,
                contractModalStatus: campaign?.contracts?.length ? 'preview' : 'negotiate',
                distributionArr: campaign?.contractPlaceholder?.distribution,
                isStepperFinished: !!campaign?.contracts?.length,
                isDistributionChecked: !!campaign?.contracts?.length,
                isLegalChecked: !!campaign?.contracts?.length,
                isEditable: campaign?.contractPlaceholder?.flags?.isEditable,
                canCounter: campaign?.contractPlaceholder?.flags?.canCounter,
                initialContract: {
                    ...state.initialContract,
                    contractItems,
                    distribution: additionalItems,
                    description: campaign?.contracts[0]?.description ?? '',
                    personalOffer: campaign?.contracts[0]?.personalOffer ?? '',
                },
                // hasLegal: (!campaign.hasExclusivity && !campaign.hasByLaws) ? false : true,
                hasDistribution: !campaign.brandedContent && !campaign.usageRights ? false : true,
                canWriteComment: [OfferStatusEnum.NEGOTIATE, OfferStatusEnum.PENDING].includes(
                    campaign?.contracts[0]?.status
                ),
                isCharity: campaign?.isCharity,
                hasUnreadComments: campaign?.contracts[0]?.hasUnreadComments,
                hasFee:
                    campaign?.contracts[0]?.hasInfluencerFee ?? campaign?.contractPlaceholder?.user?.hasInfluencerFee,
                feePercentage:
                    campaign?.contracts[0]?.influencerFeePercentage ??
                    campaign?.contractPlaceholder?.user?.influencerFeePercentage,
                isIvConnectedClub:
                    campaign?.contracts[0]?.isIvConnectedClub ?? campaign?.contractPlaceholder?.user?.isIvConnectedClub,
            };
        },
        setInitialState: () => {
            return initialContractState;
        },
        setSelectMatchedUser: (state, action) => {
            return { ...state, selectedMatchedUser: action.payload };
        },
        setDistributionItems: (state, action) => {
            const distributionArr = action.payload;
            return { ...state, distributionArr };
        },
        setDistributionCheck: (state, action) => {
            return { ...state, isDistributionChecked: action.payload };
        },
        setLegalCheck: (state, action) => {
            return { ...state, isLegalChecked: action.payload };
        },
        setFinishStepper: state => {
            return { ...state, isStepperFinished: true };
        },
        setContractItems: (state, action) => {
            const contractItems = action.payload;
            return { ...state, initialContract: { ...state.initialContract, contractItems } };
        },
        setAdditionalItems: (state, action) => {
            const distribution = action.payload;
            return { ...state, initialContract: { ...state.initialContract, distribution } };
        },
        addAdditionalItem: (state, action) => {
            const distributionItem = action.payload;
            const distributionArr: IInfluencerDistribution[] = [...state?.initialContract?.distribution];
            distributionArr.push({ ...distributionItem });
            if (distributionItem?.type === 'additional') {
                return {
                    ...state,
                    additionalItemId: state.additionalItemId + 1,
                    initialContract: { ...state.initialContract, distribution: distributionArr },
                };
            }
            return { ...state, initialContract: { ...state.initialContract, distribution: distributionArr } };
        },
        deleteAdditionItem: (state, action) => {
            const index = action.payload;
            const distributionArr = [...state?.initialContract?.distribution];
            distributionArr.splice(index, 1);
            return { ...state, initialContract: { ...state.initialContract, distribution: distributionArr } };
        },
        setPersonalOfferDescription: (state, action) => {
            return { ...state, initialContract: { ...state.initialContract, personalOffer: action.payload } };
        },
        setDescription: (state, action) => {
            return {
                ...state,
                initialContract: { ...state.initialContract, description: action.payload },
                requiredFieldChangeColor: false,
            };
        },
        setCampaignId: (state, action) => {
            return { ...state, campaignId: action.payload };
        },
        setInviteAttachment: (
            state,
            { payload: inviteAttachment }: PayloadAction<Blob[] | MediaSource[] | undefined>
        ) => {
            return { ...state, inviteAttachment };
        },
    },
});

export const {
    setActiveTab,
    setContractModalStatus,
    setOfferModalDetails,
    setInitialState,
    setLegalCheck,
    setDistributionCheck,
    setFinishStepper,
    setContractItems,
    addAdditionalItem,
    deleteAdditionItem,
    setDistributionItems,
    setAdditionalItems,
    setDescription,
    setPersonalOfferDescription,
    setCampaignId,
    setSelectMatchedUser,
    setInviteAttachment,
} = contractAgency.actions;
